import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiGet, apiSendForm, ApiError } from '../api/client';
import { todayIso } from '../lib/format';

/**
 * Add/edit through the Pagesat screen. Ports frmPagesatPages.
 *
 * Unlike the per-business dialogs, this one lets the user choose the business
 * and category, so the pickers cascade: business -> category -> article.
 *
 * The desktop version derived the business ID from the combo box position
 * (`cbBiznesi.SelectedIndex + 1`, frmPagesatPages.cs:158), which silently
 * breaks if anyone reorders the list. The ID is sent explicitly here.
 */

interface BusinessOption {
  id: number;
  slug: string;
  name: string;
  categories: { id: number; kategoria: string }[];
}

interface Detail {
  ID: number;
  data: string;
  artikulliName: string | null;
  cmimi: number;
  sasia: number | null;
  periudha: string | null;
  kategoriaID: number;
  biznesiID: number;
  file_name: string | null;
  hasFile: number;
}

interface Props {
  variant: 'aktuale' | 'planifikuara';
  rowId: number | null;
  onClose: () => void;
  onSaved: () => void;
}

export default function PagesaModal({ variant, rowId, onClose, onSaved }: Props) {
  const isEdit = rowId !== null;
  const showSasia = variant === 'aktuale';
  const showFile = variant === 'aktuale';

  const [biznesiId, setBiznesiId] = useState<number | ''>('');
  const [kategoriaId, setKategoriaId] = useState<number | ''>('');
  const [data, setData] = useState(todayIso);
  const [artikulli, setArtikulli] = useState('');
  const [cmimi, setCmimi] = useState('');
  const [sasia, setSasia] = useState('');
  const [periudha, setPeriudha] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [removeFile, setRemoveFile] = useState(false);
  const [existingFileName, setExistingFileName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const optionsQuery = useQuery({
    queryKey: ['pagesat-options'],
    queryFn: () =>
      apiGet<{ businesses: BusinessOption[]; periudhaOptions: string[] }>('/pagesat/opsionet'),
  });

  const detailQuery = useQuery({
    queryKey: ['pagesa', variant, rowId],
    queryFn: () => apiGet<Detail>(`/pagesat/${variant}/rreshti/${rowId}`),
    enabled: isEdit,
  });

  const articlesQuery = useQuery({
    queryKey: ['pagesat-articles', biznesiId, kategoriaId],
    queryFn: () =>
      apiGet<{ articles: { id: number; artikulli: string }[] }>('/pagesat/artikujt', {
        biznesiId,
        kategoriaId,
      }),
    enabled: biznesiId !== '' && kategoriaId !== '',
  });

  useEffect(() => {
    const d = detailQuery.data;
    if (!d) return;
    setBiznesiId(d.biznesiID);
    setKategoriaId(d.kategoriaID);
    setData(d.data.slice(0, 10));
    setArtikulli(d.artikulliName ?? '');
    setCmimi(String(d.cmimi ?? ''));
    setSasia(d.sasia == null ? '' : String(d.sasia));
    setPeriudha(d.periudha ?? '');
    setExistingFileName(Number(d.hasFile) === 1 ? (d.file_name ?? 'fatura') : null);
  }, [detailQuery.data]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const businesses = optionsQuery.data?.businesses ?? [];
  const categories = businesses.find((b) => b.id === biznesiId)?.categories ?? [];
  const periudhaOptions = optionsQuery.data?.periudhaOptions ?? [];

  function handleBusinessChange(value: string) {
    const id = value === '' ? '' : Number(value);
    setBiznesiId(id);
    // Category IDs are per-business, so keeping the old one would file the row
    // under an unrelated category.
    setKategoriaId('');
    setArtikulli('');
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (biznesiId === '') {
      setError('Ju lutem zgjidhni një biznes.');
      return;
    }
    if (kategoriaId === '') {
      setError('Ju lutem zgjidhni një kategori.');
      return;
    }
    if (!artikulli.trim()) {
      setError('Ju lutem zgjidhni një artikull.');
      return;
    }
    const price = Number(cmimi);
    if (cmimi.trim() === '' || !Number.isFinite(price)) {
      setError('Ju lutem vendosni një çmim të vlefshëm.');
      return;
    }

    const form = new FormData();
    form.set('biznesiId', String(biznesiId));
    form.set('kategoriaId', String(kategoriaId));
    form.set('data', data);
    form.set('artikulli', artikulli.trim());
    form.set('cmimi', String(price));
    if (showSasia && sasia.trim() !== '') form.set('sasia', String(Number(sasia)));
    if (periudha) form.set('periudha', periudha);
    if (removeFile) form.set('removeFile', 'true');
    if (file) form.set('file', file);

    setSaving(true);
    try {
      if (isEdit) {
        await apiSendForm(`/pagesat/${variant}/${rowId}`, form, 'PUT');
      } else {
        await apiSendForm(`/pagesat/${variant}`, form, 'POST');
      }
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Ruajtja dështoi. Provoni përsëri.');
    } finally {
      setSaving(false);
    }
  }

  const inputClass =
    'w-full rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100';

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center sm:p-4">
      <form
        onSubmit={handleSubmit}
        className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-2xl bg-white p-5 shadow-2xl sm:rounded-2xl"
      >
        <h2 className="mb-4 text-lg font-semibold">
          {isEdit ? 'Ndrysho rreshtin' : 'Shto rresht'}
          <span className="ml-2 text-sm font-normal text-muted">
            ({variant === 'aktuale' ? 'aktuale' : 'planifikuara'})
          </span>
        </h2>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="pg-biznesi">
              Biznesi
            </label>
            <select
              id="pg-biznesi"
              value={biznesiId}
              onChange={(e) => handleBusinessChange(e.target.value)}
              required
              className={`${inputClass} bg-white`}
            >
              <option value="">Zgjidhni…</option>
              {businesses.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="pg-kategoria">
              Kategoria
            </label>
            <select
              id="pg-kategoria"
              value={kategoriaId}
              onChange={(e) => setKategoriaId(e.target.value === '' ? '' : Number(e.target.value))}
              disabled={biznesiId === ''}
              required
              className={`${inputClass} bg-white disabled:bg-slate-50`}
            >
              <option value="">Zgjidhni…</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.kategoria}
                </option>
              ))}
            </select>
          </div>

          <div className="sm:col-span-2">
            <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="pg-artikulli">
              Artikulli
            </label>
            {/*
              Closed list — frmPagesatPages.Designer.cs:284. That dialog also
              rejects an empty selection outright (SelectedIndex == -1,
              frmPagesatPages.cs:204).
            */}
            <select
              id="pg-artikulli"
              value={artikulli}
              onChange={(e) => setArtikulli(e.target.value)}
              disabled={kategoriaId === ''}
              required
              className={`${inputClass} disabled:bg-slate-50`}
            >
              <option value="">Zgjidhni një artikull</option>
              {articlesQuery.data?.articles.map((a) => (
                <option key={a.id} value={a.artikulli}>
                  {a.artikulli}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="pg-data">
              Data
            </label>
            <input
              id="pg-data"
              type="date"
              value={data}
              onChange={(e) => setData(e.target.value)}
              required
              className={inputClass}
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="pg-cmimi">
              Çmimi
            </label>
            <input
              id="pg-cmimi"
              inputMode="decimal"
              value={cmimi}
              onChange={(e) => setCmimi(e.target.value.replace(/[^\d.]/g, ''))}
              required
              className={`tnum ${inputClass}`}
            />
          </div>

          {showSasia && (
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="pg-sasia">
                Sasia
              </label>
              <input
                id="pg-sasia"
                inputMode="decimal"
                value={sasia}
                onChange={(e) => setSasia(e.target.value.replace(/[^\d.]/g, ''))}
                className={`tnum ${inputClass}`}
              />
            </div>
          )}

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="pg-periudha">
              Periudha
            </label>
            <select
              id="pg-periudha"
              value={periudha}
              onChange={(e) => setPeriudha(e.target.value)}
              className={`${inputClass} bg-white`}
            >
              <option value="">—</option>
              {periudhaOptions.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </div>

          {showFile && (
            <div className="sm:col-span-2">
              <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="pg-file">
                Fatura
              </label>
              {existingFileName && !file && !removeFile && (
                <div className="mb-2 flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-sm">
                  <span className="truncate text-slate-700">{existingFileName}</span>
                  <button
                    type="button"
                    onClick={() => setRemoveFile(true)}
                    className="ml-2 shrink-0 text-xs font-medium text-red-600 hover:underline"
                  >
                    Hiq
                  </button>
                </div>
              )}
              {removeFile && (
                <div className="mb-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  Fatura ekzistuese do të hiqet kur të ruani.
                </div>
              )}
              <input
                id="pg-file"
                type="file"
                onChange={(e) => {
                  setFile(e.target.files?.[0] ?? null);
                  setRemoveFile(false);
                }}
                className="w-full text-sm file:mr-3 file:rounded-lg file:border-0 file:bg-slate-100 file:px-3 file:py-2 file:text-sm file:font-medium hover:file:bg-slate-200"
              />
            </div>
          )}
        </div>

        {error && (
          <div role="alert" className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Anulo
          </button>
          <button
            type="submit"
            disabled={saving}
            className="rounded-lg bg-brand-500 px-5 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-60"
          >
            {saving ? 'Duke ruajtur…' : 'Ruaj'}
          </button>
        </div>
      </form>
    </div>
  );
}
